import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Modal,
  Input,
  Row,
  Col,
  Image,
  Tooltip,
  Popconfirm,
  Switch,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import collectionService from '../services/collectionService';
import { Collection, CollectionPositionUpdate } from '../types/collections';
import CollectionForm from '../components/collections/CollectionForm';
import CollectionDetailModal from '../components/collections/CollectionDetailModal';
import { useSnackbar } from 'notistack';

const { Title } = Typography;
 

const Collections: React.FC = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [currentCollection, setCurrentCollection] = useState<Collection | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { enqueueSnackbar } = useSnackbar();

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    try {
      const filter: any = {};
      if (search) filter.search = search;
      if (statusFilter !== 'all') filter.status = statusFilter;
      const res = await collectionService.getCollections(filter);
      setCollections(res.data);
    } catch (error) {
      enqueueSnackbar('Lỗi khi tải danh sách bộ sưu tập!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  

  const showCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  const showEditModal = (collection: Collection) => {
    setCurrentCollection(collection);
    setIsEditModalVisible(true);
  };

  const showViewModal = (collection: Collection) => {
    setSelectedCollectionId(collection._id);
    setIsViewModalVisible(true);
  };

  const handleViewModalCancel = () => {
    setIsViewModalVisible(false);
    setSelectedCollectionId(null);
  };

  const handleDelete = async (collection: Collection) => {
    try {
      setLoading(true);
      await collectionService.deleteCollection(collection._id);
      enqueueSnackbar('Xóa bộ sưu tập thành công!', { variant: 'success' });
      fetchCollections();
    } catch (error) {
      enqueueSnackbar('Xóa bộ sưu tập thất bại!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: any) => {
    try {
      setLoading(true);
      await collectionService.createCollection(values);
      setIsCreateModalVisible(false);
      enqueueSnackbar('Tạo bộ sưu tập thành công!', { variant: 'success' });
      fetchCollections();
    } catch (error) {
      enqueueSnackbar('Tạo bộ sưu tập thất bại!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (values: any) => {
    if (!currentCollection) return;
    try {
      setLoading(true);
      await collectionService.updateCollection(currentCollection._id, values);
      setIsEditModalVisible(false);
      setCurrentCollection(null);
      enqueueSnackbar('Cập nhật bộ sưu tập thành công!', { variant: 'success' });
      fetchCollections();
    } catch (error) {
      enqueueSnackbar('Cập nhật bộ sưu tập thất bại!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (collection: Collection, checked: boolean) => {
    try {
      const newStatus = checked ? 'active' : 'inactive';
      await collectionService.updateCollection(collection._id, { status: newStatus });
      enqueueSnackbar(`Cập nhật trạng thái thành công!`, { variant: 'success' });
      fetchCollections();
    } catch (error) {
      enqueueSnackbar('Cập nhật trạng thái thất bại!', { variant: 'error' });
    }
  };

  // Xử lý thay đổi vị trí
  const moveItem = async (id: string, direction: 'up' | 'down') => {
    const index = collections.findIndex(collection => collection._id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === collections.length - 1)
    ) {
      return;
    }

    const newCollections = [...collections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    [newCollections[index], newCollections[targetIndex]] = [
      newCollections[targetIndex],
      newCollections[index]
    ];

    // Cập nhật vị trí
    const updatedCollections = newCollections.map((collection, idx) => ({
      ...collection,
      order: idx
    }));

    setCollections(updatedCollections);

    // Cập nhật trong backend
    try {
      const positionUpdates: CollectionPositionUpdate[] = [];
      updatedCollections.forEach(collection => {
        positionUpdates.push({
          id: collection._id,
          order: collection.order || 0
        });
      });
      await collectionService.updateCollectionPositions({ collections: positionUpdates });
      enqueueSnackbar('Cập nhật vị trí bộ sưu tập thành công!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Cập nhật vị trí bộ sưu tập thất bại!', { variant: 'error' });
      console.error(error);
      fetchCollections();
    }
  };

  const columns = [
    {
      title: 'Vị trí',
      dataIndex: 'order',
      key: 'order',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Collection) => (
        <Space direction='vertical'>
          <Button
            type='text'
            size='small'
            icon={<MenuOutlined />}
            disabled={collections.findIndex(c => c._id === record._id) === 0}
            onClick={() => moveItem(record._id, 'up')}
          >
            Lên
          </Button>
          <Button
            type='text'
            size='small'
            icon={<MenuOutlined />}
            disabled={collections.findIndex(c => c._id === record._id) === collections.length - 1}
            onClick={() => moveItem(record._id, 'down')}
          >
            Xuống
          </Button>
        </Space>
      )
    },
    {
      title: 'Tên bộ sưu tập',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (text: string, record: Collection) => (
        <Tooltip title={`Tên: ${text} - Slug: ${record.slug}`}>
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-xs text-gray-500">{record.slug}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      align: 'center' as const,
      render: (text: string) => {
        const plainText = text ? text.replace(/<[^>]*>/g, '') : '';
        return (
          <Tooltip title={plainText}>
            <div className="max-w-xs truncate">{plainText}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'images',
      key: 'images',
      align: 'center' as const,
      render: (images: string[]) => (
        <Tooltip title={`Số lượng hình ảnh: ${images?.length || 0}`}>
          <div className="flex justify-center">
            {images && images.length > 0 ? (
              <Image
                width={50}
                height={50}
                src={images[0]}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                style={{ objectFit: 'cover', borderRadius: '4px' }}
              />
            ) : (
              <div className="flex justify-center items-center w-12 h-12 text-gray-400 bg-gray-200 rounded">
                <span className="text-xs">No img</span>
              </div>
            )}
            {images && images.length > 1 && (
              <div className="flex justify-center items-center ml-1 w-6 h-6 text-xs text-blue-600 bg-blue-100 rounded">
                +{images.length - 1}
              </div>
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Số lượng',
      key: 'counts',
      align: 'center' as const,
      render: (record: Collection) => (
        <Tooltip title={`${record.products?.length || 0} sản phẩm, ${record.sections?.length || 0} sections, ${record.news?.length || 0} tin tức`}>
          <div className="space-y-1">
            <div>
              <Tag color="blue">{record.products?.length || 0} sản phẩm</Tag>
            </div>
            <div>
              <Tag color="green">{record.sections?.length || 0} sections</Tag>
            </div>
            <div>
              <Tag color="orange">{record.news?.length || 0} tin tức</Tag>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string, record: Collection) => (
        <Tooltip title={`Trạng thái: ${status === 'active' ? 'Hoạt động' : 'Không hoạt động'}`}>
          <Switch
            checked={status === 'active'}
            onChange={(checked) => handleStatusChange(record, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: Collection) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => showViewModal(record)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => showEditModal(record)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Xóa bộ sưu tập"
              description="Bạn có chắc chắn muốn xóa bộ sưu tập này?"
              onConfirm={() => handleDelete(record)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button 
                icon={<DeleteOutlined />} 
                danger 
                type="text"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      {/* Header row với bộ lọc */}
      <Card className="mb-4">
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Title level={2} className="m-0">
              Quản lý bộ sưu tập
            </Title>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder="Tìm kiếm bộ sưu tập..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{ width: 300 }}
              />
              <Select
                placeholder="Lọc theo trạng thái"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="all">Tất cả</Select.Option>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchCollections}
                loading={loading}
                shape="circle"
                title="Làm mới"
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={showCreateModal}
              >
                Thêm bộ sưu tập
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Bảng bộ sưu tập */}
      <Card>
        <Table
          columns={columns}
          dataSource={collections}
          rowKey="_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bộ sưu tập`,
            pageSizeOptions: ['10', '20', '50'],
            defaultPageSize: 10,
          }}
          bordered
        />
      </Card>

      {/* Modal tạo mới */}
      <Modal
        title="Thêm bộ sưu tập mới"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <CollectionForm
          onSubmit={handleCreate}
          loading={loading}
        />
      </Modal>

      {/* Modal chỉnh sửa */}
      <Modal
        title="Chỉnh sửa bộ sưu tập"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentCollection(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <CollectionForm
          initialValues={currentCollection || undefined}
          onSubmit={handleEdit}
          loading={loading}
        />
      </Modal>

      {/* Modal xem chi tiết */}
      <CollectionDetailModal
        visible={isViewModalVisible}
        onCancel={handleViewModalCancel}
        collectionId={selectedCollectionId}
      />
    </div>
  );
};

export default Collections; 